'use client';

import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { Popover } from '@base-ui/react/popover';
import {
  IconArrowBarToRight,
  IconHelp,
  IconReportMoney,
  IconTagStarred,
  IconUserCircle,
} from '@tabler/icons-react';

import { HeaderAnchor } from './header-link';
import {
  HEADER_OVERLAY_LAYER,
  headerDisplayName,
  headerInitials,
  MAX_MENU_TRANSACTIONS,
} from './model';
import type {
  HeaderAccountMenu,
  HeaderAccountState,
  HeaderCommandAction,
  HeaderIdentity,
  HeaderLinkAction,
  HeaderPlacement,
  HeaderSignIn,
  HeaderTransactionRow,
  HeaderUnavailableAction,
} from './model';
import { useCloseAcrossBreakpoint } from './use-close-across-breakpoint';

const PILL_CLASS =
  'inline-flex h-11 min-h-11 items-center justify-center rounded-xl border-0 bg-od-bg-primary/90 px-6 text-[16px] font-medium tracking-[-0.8px] whitespace-nowrap text-od-content-primary shadow-[0_0_0_1px_rgb(88_64_50/0.16),inset_0_0_0_1px_rgb(255_255_255/0.3),inset_0_-4px_0_0_rgb(88_64_50/0.1)] transition hover:shadow-[0_0_0_1px_rgb(88_64_50/0.3),inset_0_0_0_1px_rgb(255_255_255/0.3),inset_0_-4px_0_0_rgb(88_64_50/0.05)] active:shadow-[0_0_0_1px_rgb(88_64_50/0.3),inset_0_0_0_1px_rgb(255_255_255/0.3)] focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-od-border-focused-accent-brand';

const MENU_ITEM_CLASS =
  'flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent py-2 pr-3 pl-2 text-left text-base leading-6 tracking-[-0.014em] whitespace-nowrap text-od-content-primary no-underline outline-none transition hover:bg-od-bg-hover-primary focus-visible:bg-od-bg-hover-primary aria-disabled:cursor-default aria-disabled:hover:bg-transparent aria-[current=page]:bg-od-bg-secondary aria-[current=page]:font-medium';

function Divider() {
  return <div aria-hidden className="h-px shrink-0 bg-od-border-secondary" />;
}

export function TransactionIcon({ icon }: { icon: HeaderTransactionRow['icon'] }) {
  const Icon = icon === 'purchase' ? IconTagStarred : IconReportMoney;
  return <Icon size={20} stroke={1.5} aria-hidden />;
}

export const ACCOUNT_ICON = <IconUserCircle size={20} stroke={1.5} aria-hidden />;
export const HELP_ICON = <IconHelp size={20} stroke={1.5} aria-hidden />;
export const SIGN_OUT_ICON = <IconArrowBarToRight size={20} stroke={1.5} aria-hidden />;

export function MenuItemContent({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <>
      <span aria-hidden className="inline-flex size-5 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="min-w-0 truncate">{children}</span>
    </>
  );
}

/** Content for a control that cannot run, with its reason shown under the label. */
export function UnavailableItemContent({
  icon,
  label,
  reason,
  reasonId,
}: {
  icon: ReactNode;
  label: string;
  reason: string;
  reasonId: string;
}) {
  return (
    <>
      <span aria-hidden className="inline-flex size-5 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate">{label}</span>
        <span
          id={reasonId}
          className="text-xs leading-4 whitespace-normal text-od-content-tertiary"
        >
          {reason}
        </span>
      </span>
    </>
  );
}

function AccountAvatar({ identity, trigger }: { identity: HeaderIdentity; trigger: boolean }) {
  return (
    <span
      aria-hidden
      className={
        trigger
          ? 'inline-flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-od-bg-tertiary text-[14px] leading-none font-medium text-od-content-primary shadow-[0_0_0_1px_rgb(88_64_50/0.16),inset_0_0_0_1px_rgb(255_255_255/0.3),inset_0_-4px_0_0_rgb(88_64_50/0.1)] transition group-hover:shadow-[0_0_0_1px_rgb(88_64_50/0.3),inset_0_0_0_1px_rgb(255_255_255/0.3),inset_0_-4px_0_0_rgb(88_64_50/0.05)] group-active:shadow-[0_0_0_1px_rgb(88_64_50/0.3),inset_0_0_0_1px_rgb(255_255_255/0.3)]'
          : 'inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-transparent text-xs font-medium text-[#3c3733] [background:linear-gradient(90deg,#eae8e0,#d7d4cd)_border-box_padding-box,linear-gradient(27.77deg,rgb(249_248_245/60%)_8.46%,transparent_26.47%)_border-box,linear-gradient(-172.67deg,rgb(249_248_245/70%)_17.64%,transparent_43.76%)_border-box,linear-gradient(#bdbab3,#bdbab3)_border-box]'
      }
    >
      {headerInitials(identity)}
    </span>
  );
}

export function AccountIdentity({
  identity,
  placement,
}: {
  identity: HeaderIdentity;
  placement: Extract<HeaderPlacement, 'menu' | 'drawer'>;
}) {
  const name = headerDisplayName(identity);
  const email = identity.email.trim();
  const drawer = placement === 'drawer';

  return (
    <div
      className={
        drawer
          ? 'flex items-center gap-3 border-b border-od-bg-hover-primary pb-3'
          : 'flex items-center gap-2.5 px-2 py-3'
      }
    >
      <AccountAvatar identity={identity} trigger={false} />
      <div className="min-w-0 flex-1">
        {name ? (
          <div className="flex min-w-0 items-center gap-2">
            <p
              className={`m-0 min-w-0 truncate font-medium text-od-content-primary ${drawer ? 'text-base leading-[22px]' : 'text-sm leading-[1.25] tracking-[-0.014em]'}`}
            >
              {name}
            </p>
            {identity.badge === 'agent' ? (
              <span className="inline-flex min-h-4.5 shrink-0 items-center rounded-full bg-[#e0e9ff] px-1.5 py-0.5 text-[11px] leading-3.5 font-medium text-[#00288b]">
                Agent
              </span>
            ) : null}
          </div>
        ) : null}
        {email ? (
          <p className="m-0 truncate text-[13px] leading-5 text-od-content-secondary">{email}</p>
        ) : null}
      </div>
    </div>
  );
}

const PLACEHOLDER_PILL_CLASS =
  'inline-flex h-11 items-center justify-center rounded-xl bg-[rgb(88_64_50/0.06)] text-[16px] font-medium tracking-[-0.8px] shadow-[0_0_0_1px_rgb(88_64_50/0.1),inset_0_-4px_0_0_rgb(88_64_50/0.05)]';

export function AccountLoading() {
  return (
    <output aria-label="Loading your account" className="flex h-11 w-full items-center">
      <span
        aria-hidden
        className={`${PLACEHOLDER_PILL_CLASS} w-full animate-site-header-account-placeholder px-6`}
      >
        <span className="invisible">Sign in</span>
      </span>
    </output>
  );
}

function HeaderAccountPlaceholder({ loading }: { loading: boolean }) {
  const [mounted, setMounted] = useState(loading);
  const layerRef = useRef<HTMLDivElement>(null);

  if (loading && !mounted) setMounted(true);

  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (loading || !mounted || !layer) return undefined;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const from = Number(getComputedStyle(layer).opacity);
    if (motion.matches || !(from > 0.01) || typeof layer.animate !== 'function') {
      setMounted(false);
      return undefined;
    }
    layer.style.animation = 'none';
    const fade = layer.animate([{ opacity: from }, { opacity: 0 }], {
      duration: 300,
      easing: 'ease-in-out',
      fill: 'forwards',
    });
    let active = true;
    const finish = () => {
      if (active) setMounted(false);
    };
    const onMotionChange = () => {
      if (motion.matches) finish();
    };
    fade.onfinish = finish;
    motion.addEventListener('change', onMotionChange);
    return () => {
      // A cancelled refresh fade must not remove the next loading layer.
      active = false;
      fade.onfinish = null;
      fade.cancel();
      layer.style.animation = '';
      motion.removeEventListener('change', onMotionChange);
    };
  }, [loading, mounted]);

  if (!mounted) return null;

  return (
    <div
      ref={layerRef}
      aria-hidden
      data-header-account-placeholder
      className="pointer-events-none absolute inset-y-0 right-0 flex animate-site-header-account-placeholder items-center"
    >
      <span data-account-shape="signed-out" className={`${PLACEHOLDER_PILL_CLASS} px-6`}>
        <span className="invisible">Sign in</span>
      </span>
      <span data-account-shape="signed-in" className="hidden items-center gap-2">
        <span className={`${PLACEHOLDER_PILL_CLASS} px-6`}>
          <span className="invisible">Dashboard</span>
        </span>
        <span className={`${PLACEHOLDER_PILL_CLASS} size-11 px-0`} />
      </span>
    </div>
  );
}

export function SignInLink({
  signIn,
  placement,
  onSelect,
}: {
  signIn: HeaderSignIn;
  placement: Extract<HeaderPlacement, 'bar' | 'drawer'>;
  onSelect?: () => void;
}) {
  return (
    <HeaderAnchor
      ref={placement === 'bar' ? signIn.barRef : undefined}
      action={signIn.action}
      placement={placement}
      className={placement === 'drawer' ? `${PILL_CLASS} w-full` : PILL_CLASS}
      onClick={onSelect}
    >
      Sign in
    </HeaderAnchor>
  );
}

function DashboardLink({ dashboard }: { dashboard: HeaderAccountMenu['dashboard'] }) {
  return (
    <HeaderAnchor
      action={dashboard.action}
      placement="bar"
      aria-label="View Dashboard"
      aria-current={dashboard.current ? 'page' : undefined}
      className={PILL_CLASS}
    >
      Dashboard
    </HeaderAnchor>
  );
}

function MenuLinkRow({
  action,
  icon,
  label,
  close,
  children,
}: {
  action: HeaderLinkAction;
  icon: ReactNode;
  /** Replaces the visible text as the accessible name when the text alone is ambiguous. */
  label?: string;
  close: () => void;
  children: ReactNode;
}) {
  return (
    <HeaderAnchor
      action={action}
      placement="menu"
      aria-label={label}
      className={MENU_ITEM_CLASS}
      onClick={close}
    >
      <MenuItemContent icon={icon}>{children}</MenuItemContent>
    </HeaderAnchor>
  );
}

function MenuCommandRow({
  command,
  label,
  icon,
  runCommand,
}: {
  command: HeaderCommandAction | HeaderUnavailableAction;
  label: string;
  icon: ReactNode;
  runCommand: (command: HeaderCommandAction) => void;
}) {
  const reasonId = useId();
  const handleClick = useCallback(() => {
    if (command.kind === 'command') runCommand(command);
  }, [command, runCommand]);
  if (command.kind === 'unavailable') {
    // aria-disabled keeps the control and its reason reachable from the keyboard.
    return (
      <button
        type="button"
        className={MENU_ITEM_CLASS}
        aria-disabled="true"
        aria-label={label}
        aria-describedby={reasonId}
      >
        <UnavailableItemContent
          icon={icon}
          label={label}
          reason={command.reason}
          reasonId={reasonId}
        />
      </button>
    );
  }
  return (
    <button type="button" className={MENU_ITEM_CLASS} onClick={handleClick}>
      <MenuItemContent icon={icon}>{label}</MenuItemContent>
    </button>
  );
}

function AccountMenu({ identity, menu }: { identity: HeaderIdentity; menu: HeaderAccountMenu }) {
  const [open, setOpen] = useState(false);
  const visibleTransactions = menu.transactions.slice(0, MAX_MENU_TRANSACTIONS);
  const showMore = menu.transactions.length > MAX_MENU_TRANSACTIONS;
  const hasTransactionSection = menu.transactions.length > 0 || menu.transactionLinks.length > 0;
  const close = useCallback(() => setOpen(false), []);
  const runCommand = useCallback((command: HeaderCommandAction) => {
    setOpen(false);
    command.run();
  }, []);
  useCloseAcrossBreakpoint({ open, close, openerVisibleOnDesktop: true });

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        aria-label="Your account menu"
        className="group inline-flex size-11 cursor-pointer items-center justify-center overflow-visible border-0 bg-transparent p-0 outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-od-border-focused-accent-brand"
        onClick={menu.onTriggerPress}
      >
        <AccountAvatar identity={identity} trigger />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner sideOffset={12} align="end" className={HEADER_OVERLAY_LAYER}>
          <Popover.Popup
            role="dialog"
            aria-label="Account menu"
            className="flex max-h-(--available-height) w-67 flex-col gap-1 overflow-y-auto rounded-xl border border-od-border-primary bg-od-bg-primary p-1 shadow-lg outline-none"
          >
            <AccountIdentity identity={identity} placement="menu" />
            <Divider />
            {visibleTransactions.map((row) => (
              <MenuLinkRow
                key={row.key}
                action={row.action}
                icon={<TransactionIcon icon={row.icon} />}
                close={close}
              >
                {row.label}
              </MenuLinkRow>
            ))}
            {showMore ? (
              <MenuLinkRow
                action={menu.moreTransactions}
                icon={null}
                label="More homes"
                close={close}
              >
                More
              </MenuLinkRow>
            ) : null}
            {menu.transactionLinks.map((link) => (
              <MenuLinkRow key={link.key} action={link.action} icon={null} close={close}>
                {link.label}
              </MenuLinkRow>
            ))}
            {hasTransactionSection ? <Divider /> : null}
            <MenuLinkRow action={menu.account} icon={ACCOUNT_ICON} close={close}>
              Account
            </MenuLinkRow>
            <Divider />
            <MenuLinkRow action={menu.helpCenter} icon={HELP_ICON} close={close}>
              Help center
            </MenuLinkRow>
            <MenuCommandRow
              command={menu.signOut}
              label="Sign out"
              icon={SIGN_OUT_ICON}
              runCommand={runCommand}
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

function BarAccountState({ state }: { state: HeaderAccountState }) {
  if (state.status === 'loading') return <output aria-label="Loading your account" />;
  if (state.status === 'authenticated') {
    return (
      <>
        <DashboardLink dashboard={state.menu.dashboard} />
        <AccountMenu key={state.identity.key} identity={state.identity} menu={state.menu} />
      </>
    );
  }
  return <>{state.signIn ? <SignInLink signIn={state.signIn} placement="bar" /> : null}</>;
}

export function HeaderAccount({ state }: { state: HeaderAccountState }) {
  return (
    <div
      data-header-account-controls
      className="relative flex h-11 w-49 shrink-0 items-center justify-end gap-2"
    >
      <HeaderAccountPlaceholder loading={state.status === 'loading'} />
      {state.status === 'loading' ? (
        <BarAccountState state={state} />
      ) : (
        <div className="relative flex animate-site-header-account-reveal items-center justify-end gap-2">
          <BarAccountState state={state} />
        </div>
      )}
    </div>
  );
}
